/* src/components/profile/SetUsernameModal.js */
import React, { useState, useEffect, useCallback } from 'react';

/**
 * Modal component for setting a username for an existing profile.
 */
const SetUsernameModal = ({
    isOpen,
    onClose,
    onSave, // Async function(username) => Promise<boolean> (returns true on success, false on failure)
    profileData, // Profile object ({ id, username?, ...}) passed for context
    externalError // Error message from the context (e.g., "Username taken")
}) => {
    const [username, setUsername] = useState('');
    const [validationError, setValidationError] = useState(''); // Client-side validation error
    const [isSaving, setIsSaving] = useState(false);
    const [internalError, setInternalError] = useState(null); // Error state specifically for this modal

    // Effect to clear state when the modal is closed or opened for a new profile
    useEffect(() => {
        if (isOpen) {
            setUsername('');
            setValidationError('');
            setInternalError(null); // Clear internal error
            setIsSaving(false);
        }
    }, [isOpen, profileData]); // Reset when modal opens or profile changes

    // Effect to display errors coming from the parent context (e.g., uniqueness failure)
    useEffect(() => {
        if (externalError) {
            setInternalError(externalError); // Display the error passed from the parent
        } else {
            setInternalError(null); // Clear internal error if external error is cleared
        }
    }, [externalError]); // Watch for changes in the external error prop

    /** Client-side Username Validation Function */
    const validateUsername = useCallback((uname) => {
        const trimmed = uname.trim();
        if (trimmed.length < 3 || trimmed.length > 25) {
            return "Username must be 3-25 characters long.";
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
            return "Only letters, numbers, underscores (_), and dashes (-) allowed.";
        }
        return ''; // No error
    }, []);

    const handleInputChange = (e) => {
        const newUsername = e.target.value;
        setUsername(newUsername);
        setValidationError(validateUsername(newUsername)); // Validate as user types
        setInternalError(null); // Clear internal/external error when user types
    };

    const handleSaveClick = async () => {
        const currentValidationError = validateUsername(username);
        if (currentValidationError) {
            setValidationError(currentValidationError);
            return; // Don't proceed if client validation fails
        }
        setValidationError('');
        setInternalError(null);
        setIsSaving(true);

        try {
            const success = await onSave(username.trim()); // Call the async save function passed via props
            // Parent (ProfileSelectionScreen) handles navigation/state update on success.
            // The modal relies on the parent returning true/false.
            // If `onSave` throws an error, it will be caught below.
            if (!success) {
                // If onSave returns false, it implies an error occurred (e.g., username taken)
                // The error message should ideally be set in the parent's state (externalError)
                // and displayed via the useEffect above. If not, show a generic message.
                if (!externalError) {
                    setInternalError("Failed to save username. It might already be taken.");
                }
            }
            // On success, the parent component will typically close the modal by setting isOpen=false
        } catch (error) {
            // Catch unexpected errors from the onSave promise itself
            console.error("Error calling onSave from SetUsernameModal:", error);
            setInternalError("An unexpected error occurred while saving.");
        } finally {
            setIsSaving(false);
        }
    };

    // Prevent rendering if not open
    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-60 flex items-center justify-center p-4 animate-fadeIn">
            <div
                className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md transform transition-all animate-scaleIn"
                role="dialog"
                aria-modal="true"
                aria-labelledby="setUsernameModalTitle"
            >
                <h2 id="setUsernameModalTitle" className="text-xl font-semibold mb-3 text-gray-800">
                    Set Your Unique Username
                </h2>

                {profileData && (
                    <p className="text-sm text-gray-600 mb-4">
                        Setting username for profile: <span className="font-medium">{profileData.username || `ID: ${profileData.id.substring(0,6)}...`}</span>
                    </p>
                )}

                {/* Username Input */}
                <div>
                    <label htmlFor="migrateUsername" className="block text-sm font-medium text-gray-700 mb-1 sr-only">
                        Username
                    </label>
                    <input
                        type="text"
                        id="migrateUsername"
                        value={username}
                        onChange={handleInputChange}
                        placeholder="Choose your username"
                        required
                        maxLength={25}
                        className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                            validationError || internalError ? 'border-red-500' : 'border-gray-300'
                        }`}
                        aria-label="Username"
                        aria-describedby="migrate-username-constraints migrate-username-error"
                        disabled={isSaving}
                    />
                </div>

                {/* Constraint/Visibility Message */}
                <p id="migrate-username-constraints" className="text-xs text-gray-500 mt-1">
                    Unique, 3-25 characters (a-z, A-Z, 0-9, _, -). <strong className="text-orange-600">Cannot be changed later.</strong> Visible to others.
                </p>

                {/* Display Validation or Save Error */}
                {(validationError || internalError) && (
                    <p id="migrate-username-error" className="text-sm text-red-600 mt-2" role="alert">
                        {validationError || internalError}
                    </p>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveClick}
                        disabled={isSaving || !!validationError || !username.trim()}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? 'Saving...' : 'Save Username'}
                    </button>
                </div>
            </div>
            {/* Simple animation styles (can be moved to a global CSS file) */}
            <style jsx global>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                .animate-fadeIn { animation: fadeIn 0.2s ease-out forwards; }
                @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                .animate-scaleIn { animation: scaleIn 0.2s ease-out forwards; }
            `}</style>
        </div>
    );
};

export default SetUsernameModal;